import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import { supabase } from '../../../lib/supabase';

const CreateSubscriptionModal = ({ isOpen, onClose, onCreate }) => {
  const [formData, setFormData] = useState({
    customerName: '',
    customerEmail: '',
    plan: 'pro',
    amount: 29.00,
    currency: 'USD',
    interval: 'monthly',
    trialDays: 0,
    enableTrial: false
  });

  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);

  if (!isOpen) return null;

  const planLabel = (key) => {
    const labels = { basic: 'Basic Plan', pro: 'Pro Plan', enterprise: 'Enterprise Plan' };
    return labels[key] || key;
  };

  const handleCreate = async () => {
    setCreating(true);
    setError(null);
    try {
      const { data, error: fnError } = await supabase.functions.invoke('create-subscription', {
        body: {
          customerName: formData.customerName,
          customerEmail: formData.customerEmail,
          plan: planLabel(formData.plan),
          amount: formData.amount,
          currency: formData.currency,
          interval: formData.interval,
          trialDays: formData.enableTrial ? formData.trialDays : 0,
        },
      });

      if (fnError) throw fnError;
      if (data?.error) throw new Error(data.error + (data.details ? ': ' + data.details : ''));

      onCreate(data);
    } catch (err) {
      console.error('Error creating subscription:', err);
      setError(err?.message || 'Failed to create subscription. Please try again.');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-background/80 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-card border border-border rounded-lg shadow-elevation-lg w-full max-w-2xl mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h3 className="text-lg font-semibold text-foreground">Create New Subscription</h3>
            <p className="text-sm text-muted-foreground">Set up a recurring payment plan</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-md transition-smooth"
          >
            <Icon name="X" size={20} className="text-muted-foreground" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {error && (
            <div className="flex items-start gap-3 p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
              <Icon name="AlertCircle" size={16} className="text-destructive mt-0.5 shrink-0" />
              <p className="text-sm text-destructive">{error}</p>
            </div>
          )}

          {/* Customer Information */}
          <div className="space-y-4">
            <h4 className="text-sm font-semibold text-foreground flex items-center gap-2">
              <Icon name="User" size={16} />
              Customer Information
            </h4>

            <Input
              label="Customer Name"
              value={formData?.customerName}
              onChange={(e) => setFormData({ ...formData, customerName: e?.target?.value })}
              placeholder="John Doe"
              required
            />

            <Input
              label="Customer Email"
              type="email"
              value={formData?.customerEmail}
              onChange={(e) => setFormData({ ...formData, customerEmail: e?.target?.value })}
              placeholder="john@example.com"
              required
            />
          </div>

          {/* Plan Configuration */}
          <div className="space-y-4">
            <h4 className="text-sm font-semibold text-foreground flex items-center gap-2">
              <Icon name="Layers" size={16} />
              Plan Configuration
            </h4>

            <Select
              label="Plan"
              options={[
                { value: 'basic', label: 'Basic Plan' },
                { value: 'pro', label: 'Pro Plan' },
                { value: 'enterprise', label: 'Enterprise Plan' }
              ]}
              value={formData?.plan}
              onChange={(value) => {
                const amounts = { basic: 9.00, pro: 29.00, enterprise: 99.00 };
                setFormData({ ...formData, plan: value, amount: amounts?.[value] });
              }}
            />

            <Input
              label="Amount"
              type="number"
              value={formData?.amount}
              onChange={(e) => setFormData({ ...formData, amount: parseFloat(e?.target?.value) })}
              placeholder="29.00"
              step="0.01"
              min="0"
            />

            <Select
              label="Currency"
              options={[
                { value: 'USD', label: 'USD - US Dollar' },
                { value: 'EUR', label: 'EUR - Euro' },
                { value: 'GBP', label: 'GBP - British Pound' },
                { value: 'INR', label: 'INR - Indian Rupee' }
              ]}
              value={formData?.currency}
              onChange={(value) => setFormData({ ...formData, currency: value })}
            />

            <Select
              label="Billing Interval"
              options={[
                { value: 'monthly', label: 'Monthly' },
                { value: 'yearly', label: 'Yearly' }
              ]}
              value={formData?.interval}
              onChange={(value) => setFormData({ ...formData, interval: value })}
            />
          </div>

          {/* Trial Period */}
          <div className="space-y-4">
            <h4 className="text-sm font-semibold text-foreground flex items-center gap-2">
              <Icon name="Clock" size={16} />
              Trial Period
            </h4>

            <Checkbox
              label="Enable Free Trial"
              checked={formData?.enableTrial}
              onChange={(e) => setFormData({ ...formData, enableTrial: e?.target?.checked })}
              description="Offer a trial period before charging"
            />

            {formData?.enableTrial && (
              <Input
                label="Trial Duration (days)"
                type="number"
                value={formData?.trialDays}
                onChange={(e) => setFormData({ ...formData, trialDays: parseInt(e?.target?.value) })}
                placeholder="14"
                min="1"
                max="90"
              />
            )}
          </div>

          {/* Summary */}
          <div className="p-4 bg-muted/30 rounded-lg">
            <h4 className="text-sm font-semibold text-foreground mb-3">Summary</h4>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Customer</span>
                <span className="text-foreground font-medium">{formData?.customerName || 'Not set'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Plan</span>
                <span className="text-foreground font-medium">
                  {formData?.plan === 'basic' ? 'Basic' : formData?.plan === 'pro' ? 'Pro' : 'Enterprise'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Amount</span>
                <span className="text-foreground font-medium">
                  {new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: formData?.currency
                  })?.format(formData?.amount)} / {formData?.interval}
                </span>
              </div>
              {formData?.enableTrial && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Trial Period</span>
                  <span className="text-foreground font-medium">{formData?.trialDays} days</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-border bg-muted/30 flex items-center justify-between">
          <Button variant="outline" onClick={onClose} disabled={creating}>
            Cancel
          </Button>
          <Button
            iconName={creating ? "Loader2" : "Plus"}
            iconClassName={creating ? "animate-spin" : ""}
            onClick={handleCreate}
            disabled={creating || !formData?.customerName || !formData?.customerEmail}
          >
            {creating ? 'Creating...' : 'Create Subscription'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CreateSubscriptionModal;
